"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Phone, Search, FileText, Calendar, Hammer, ClipboardCheck, House as Home, Headset, ShieldCheck, Star, Clock, Users } from 'lucide-react';
const steps = [{
  id: "01",
  title: "Initial Contact",
  description: "Get in touch by phone, email, or through our contact form. We'll discuss your requirements and arrange a convenient time to assess the work.",
  icon: Phone
}, {
  id: "02",
  title: "Site Visit & Consultation",
  description: "For larger projects, we carry out a site visit to understand your needs, inspect the property, and recommend the most suitable electrical solutions.",
  icon: Search
}, {
  id: "03",
  title: "Detailed Quotation",
  description: "We provide a clear, transparent quotation outlining the scope of work, materials required, timelines, and costs, ensuring no surprises.",
  icon: FileText
}, {
  id: "04",
  title: "Planning & Scheduling",
  description: "Once approved, we schedule the work at a time that suits you. We coordinate all aspects of the project to ensure minimal disruption.",
  icon: Calendar
}, {
  id: "05",
  title: "Professional Installation",
  description: "Our qualified electricians complete all work to the highest standards, following current regulations and industry best practices.",
  icon: Hammer
}, {
  id: "06",
  title: "Testing & Certification",
  description: "Every installation is thoroughly tested and inspected to ensure it is safe, compliant, and performing as intended. Relevant certification is provided.",
  icon: ClipboardCheck
}, {
  id: "07",
  title: "Project Completion",
  description: "We walk you through the completed work, answer any questions, and ensure you are completely satisfied before signing off on the project.",
  icon: Home
}, {
  id: "08",
  title: "Ongoing Support",
  description: "Our relationship doesn't end when the job is finished. We're available for future maintenance, upgrades, advice, and support whenever you need us.",
  icon: Headset
}];
const values = [{
  title: "Experienced",
  description: "Skilled professionals with years of industry experience.",
  icon: Star
}, {
  title: "Quality Assured",
  description: "All work completed to the highest standards.",
  icon: ShieldCheck
}, {
  title: "Safe & Compliant",
  description: "Fully compliant with current regulations and safety standards.",
  icon: ClipboardCheck
}, {
  title: "On Time",
  description: "We respect your time and deliver projects on schedule.",
  icon: Clock
}, {
  title: "Customer Focused",
  description: "Your satisfaction is our priority, every step of the way.",
  icon: Users
}];
export default function TheEcoShineProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224836" id="our-working-process" className="py-24 md:py-32 bg-background text-foreground relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-4xl mx-auto text-center mb-20 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-accent/10 text-accent text-sm font-mono mb-6 border border-accent/20">
            <Hammer className="w-4 h-4" />
            <span>FROM FIRST CONTACT TO FINAL HANDOVER</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-default font-extrabold text-foreground mb-6 tracking-tighter uppercase">
            Our <span className="text-accent">Working</span> Process
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default max-w-2xl mx-auto">
            A clear, professional process that ensures your project is completed safely, efficiently, and to the highest standard.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          {steps.map((step, index) => {
          const Icon = step.icon;
          return <Card key={step.id} data-index={index} className="group relative border-border/50 bg-background hover:border-accent/50 transition-all duration-300 rounded-md shadow-md">
                <CardContent className="p-8 flex flex-col items-center text-center gap-4">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-mono font-bold text-lg mb-2">
                      {step.id}
                    </div>
                    <div className="absolute -right-2 -bottom-2 w-10 h-10 rounded-md bg-accent text-accent-foreground flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold uppercase tracking-tight mt-4">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="bg-primary text-primary-foreground rounded-lg overflow-hidden shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-4 p-8 lg:p-12 bg-accent text-accent-foreground flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-primary/10">
              <ShieldCheck className="w-12 h-12 mb-6" />
              <h4 className="text-2xl font-extrabold uppercase tracking-tighter mb-6 leading-none">
                AD Electrical Delivers A Professional, Reliable, and Hassle-Free Service You Can Trust.
              </h4>
              <Button asChild className="w-fit bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-bold uppercase tracking-wider">
                <Link to="tel:+353894000413">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Link>
              </Button>
            </div>
            <div className="lg:col-span-8 p-8 lg:p-12 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
              {values.map((val, idx) => {
              const ValIcon = val.icon;
              return <div key={idx} data-index={idx} className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-md bg-accent/20 text-accent">
                        <ValIcon className="w-5 h-5" />
                      </div>
                      <h5 className="font-bold uppercase text-sm tracking-wider">{val.title}</h5>
                    </div>
                    <p className="text-xs text-primary-foreground/70 leading-relaxed">
                      {val.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}